import { useState } from 'react'
import { Listbox } from '@headlessui/react'
import { ChevronUpDownIcon, CheckIcon } from '@heroicons/react/20/solid'

function formatCompanyName(name) {
	return name.replace(/_/g, ' ').toUpperCase()
}

const ALERT_TYPES = [
	{ key: 'newLoadAlert', label: 'New Load Alert' },
	{ key: 'trailerReadyAlert', label: 'Trailer Ready' },
	{ key: 'thirtyMinutesLeftAlert', label: '30 Min Left' },
	{ key: 'sixtyMinutesLeftAlert', label: '60 Min Left' },
	{ key: 'driverCheckedInAlert', label: 'Driver Checked In' },
	{ key: 'bobtailApproveAlert', label: 'Bobtail Approve' },
	{ key: 'roadRestrictionAlert', label: 'Road Restriction' },
	{ key: 'noEarlyArrivalAlert', label: 'No Early Arrival' },
	{ key: 'newNoteAlert', label: 'New Note' },
	{ key: 'completedTripAlert', label: 'Completed Trip' },
	{ key: 'lateEtaAlert', label: 'Late ETA' },
	{ key: 'fifteenMinutesToCheckoutAlert', label: '15 Min to Checkout' },
]

function CompanyNotificationsCard({
	isDarkMode,
	companySettings,
	groups,
	onUpdateSettings,
	onDeleteCompany,
	onTestNotification,
}) {
	const [updating, setUpdating] = useState({})
	const [expandedCompany, setExpandedCompany] = useState(null)
	const [deleteConfirm, setDeleteConfirm] = useState(null)
	const [deleting, setDeleting] = useState(false)
	const [testing, setTesting] = useState({})
	const [dropdownDirection, setDropdownDirection] = useState({})

	const measureDropdownDirection = (companyName, btnEl) => {
		if (!btnEl) return
		const rect = btnEl.getBoundingClientRect()
		const spaceBelow = window.innerHeight - rect.bottom
		const dropUp = spaceBelow < 260 && rect.top > spaceBelow
		setDropdownDirection(prev => ({
			...prev,
			[companyName]: dropUp ? 'up' : 'down',
		}))
	}

	const textColor = isDarkMode ? 'text-white' : 'text-gray-900'
	const subTextColor = isDarkMode ? 'text-gray-400' : 'text-gray-500'
	const cardBg = isDarkMode ? 'bg-gray-700' : 'bg-white'
	const borderColor = isDarkMode ? 'border-gray-600' : 'border-gray-200'

	const handleToggleNotifications = async (companyName, currentSettings) => {
		const newValue = !currentSettings.isNotificationsOn
		setUpdating(prev => ({ ...prev, [companyName]: true }))

		const settingsUpdate = { isNotificationsOn: newValue }

		if (!newValue) {
			ALERT_TYPES.forEach(alert => {
				settingsUpdate[alert.key] = false
			})
		} else {
			const allOff = ALERT_TYPES.every(alert => !currentSettings[alert.key])
			if (allOff) {
				ALERT_TYPES.forEach(alert => {
					settingsUpdate[alert.key] = true
				})
			}
		}

		const groupId = newValue && !currentSettings.selectedTelegramGroup && groups.length > 0
			? groups[0].groupId
			: currentSettings.selectedTelegramGroup

		await onUpdateSettings(companyName, settingsUpdate, groupId)
		setUpdating(prev => ({ ...prev, [companyName]: false }))
	}

	const handleGroupChange = async (companyName, groupId, currentSettings) => {
		setUpdating(prev => ({ ...prev, [companyName]: true }))
		const notificationsOn = groupId ? currentSettings.isNotificationsOn : false
		await onUpdateSettings(
			companyName,
			{ isNotificationsOn: notificationsOn },
			groupId || null
		)
		setUpdating(prev => ({ ...prev, [companyName]: false }))
	}

	const handleAlertToggle = async (companyName, alertKey, currentSettings) => {
		const newValue = !currentSettings[alertKey]
		setUpdating(prev => ({ ...prev, [`${companyName}-${alertKey}`]: true }))

		const settingsUpdate = { [alertKey]: newValue }

		if (newValue && !currentSettings.isNotificationsOn) {
			settingsUpdate.isNotificationsOn = true
		} else if (!newValue) {
			const allOthersOff = ALERT_TYPES.every(
				alert => alert.key === alertKey || !currentSettings[alert.key]
			)
			if (allOthersOff) {
				settingsUpdate.isNotificationsOn = false
			}
		}

		await onUpdateSettings(companyName, settingsUpdate, currentSettings.selectedTelegramGroup)
		setUpdating(prev => ({ ...prev, [`${companyName}-${alertKey}`]: false }))
	}

	const handleDeleteConfirm = async () => {
		if (!deleteConfirm) return
		setDeleting(true)
		await onDeleteCompany(deleteConfirm)
		setDeleting(false)
		setDeleteConfirm(null)
	}

	const handleTest = async (companyName, groupId) => {
		setTesting(prev => ({ ...prev, [companyName]: true }))
		await onTestNotification(companyName, groupId)
		setTesting(prev => ({ ...prev, [companyName]: false }))
	}

	return (
		<div className="space-y-2">
			<h3 className={`text-sm font-medium ${textColor}`}>
				Company Notifications ({companySettings.length})
			</h3>

			<div className="space-y-2">
				{companySettings.map(settings => {
					const companyName = settings.companyName
					const isUpdating = updating[companyName]
					const noGroups = groups.length === 0
					const isExpanded = expandedCompany === companyName

					return (
						<div
							key={settings._id || companyName}
							className={`${cardBg} border ${borderColor} rounded-lg`}
						>
							<div
								className="flex items-center justify-between px-4 py-3 cursor-pointer"
								onClick={() => setExpandedCompany(isExpanded ? null : companyName)}
							>
								<div className="flex items-center gap-2">
									<svg
										className={`w-4 h-4 ${subTextColor} transition-transform ${isExpanded ? 'rotate-90' : ''}`}
										fill="none"
										viewBox="0 0 24 24"
										stroke="currentColor"
										strokeWidth={2}
									>
										<path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
									</svg>
									<p className={`text-xs ${textColor}`}>
										{formatCompanyName(companyName)}
									</p>
								</div>

								<div className="flex items-center gap-4" onClick={e => e.stopPropagation()}>
									<div className="flex items-center gap-2">
										<span className={`text-xs ${settings.isNotificationsOn ? 'text-blue-500' : subTextColor}`}>
											{settings.isNotificationsOn ? 'Notification on' : 'Notification off'}
										</span>
										<div
											className={`relative inline-block w-10 h-5 rounded-full transition ${
												settings.isNotificationsOn
													? 'bg-blue-500'
													: isDarkMode
													? 'bg-gray-600'
													: 'bg-gray-300'
											} ${isUpdating || noGroups ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
											onClick={() => !(isUpdating || noGroups) && handleToggleNotifications(companyName, settings)}
										>
											<div
												className={`absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
													settings.isNotificationsOn ? 'translate-x-5' : 'translate-x-0'
												}`}
											/>
										</div>
									</div>

									<Listbox
										value={settings.selectedTelegramGroup ? String(settings.selectedTelegramGroup) : ''}
										onChange={val => handleGroupChange(companyName, val || null, settings)}
										disabled={isUpdating || noGroups}
									>
										<div className="relative w-[280px]">
											<Listbox.Button
												onClick={e => measureDropdownDirection(companyName, e.currentTarget)}
												className={`relative w-full text-left text-xs rounded border pl-2 pr-7 py-1 cursor-default ${
													isDarkMode
														? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600'
														: 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50'
												} ${isUpdating || noGroups ? 'opacity-50 cursor-not-allowed' : ''}`}
											>
												{(() => {
													const selected = groups.find(
														g => String(g.groupId) === String(settings.selectedTelegramGroup),
													)
													if (!selected) {
														return <span className="block truncate">No group</span>
													}
													return (
														<span className="block truncate">
															{selected.groupName}
															{selected.isOwn === false && (
																<span className={`ml-1 ${subTextColor}`}>
																	· {selected.ownerName || selected.ownerEmail}
																</span>
															)}
														</span>
													)
												})()}
												<span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
													<ChevronUpDownIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
												</span>
											</Listbox.Button>
											<Listbox.Options
												className={`absolute z-[100] max-h-60 w-full overflow-auto rounded-md py-1 text-xs shadow-lg focus:outline-none ${
													dropdownDirection[companyName] === 'up'
														? 'bottom-full mb-1'
														: 'top-full mt-1'
												} ${
													isDarkMode
														? 'bg-gray-700 border border-gray-600'
														: 'bg-white border border-gray-300'
												}`}
											>
												<Listbox.Option
													value=""
													className={({ active }) =>
														`relative cursor-default select-none py-1.5 pl-8 pr-3 ${
															active
																? isDarkMode
																	? 'bg-gray-600 text-white'
																	: 'bg-gray-100 text-gray-900'
																: textColor
														}`
													}
												>
													{({ selected }) => (
														<>
															<span className={`block truncate ${selected ? 'font-medium' : ''}`}>
																No group
															</span>
															{selected && (
																<span className="absolute inset-y-0 left-0 flex items-center pl-2 text-gray-400">
																	<CheckIcon className="h-4 w-4" aria-hidden="true" />
																</span>
															)}
														</>
													)}
												</Listbox.Option>
												{groups.map(group => (
													<Listbox.Option
														key={group.groupId}
														value={String(group.groupId)}
														className={({ active }) =>
															`relative cursor-default select-none py-1.5 pl-8 pr-3 ${
																active
																	? isDarkMode
																		? 'bg-gray-600 text-white'
																		: 'bg-gray-100 text-gray-900'
																	: textColor
															}`
														}
													>
														{({ selected }) => (
															<>
																<div className="flex flex-col">
																	<span className={`truncate ${selected ? 'font-medium' : ''}`}>
																		{group.groupName}
																	</span>
																	{group.isOwn === false && (
																		<span className={`truncate text-[10px] ${subTextColor}`}>
																			{group.ownerName ? `${group.ownerName} · ` : ''}
																			{group.ownerEmail}
																		</span>
																	)}
																</div>
																{selected && (
																	<span className="absolute inset-y-0 left-0 flex items-center pl-2 text-gray-400">
																		<CheckIcon className="h-4 w-4" aria-hidden="true" />
																	</span>
																)}
															</>
														)}
													</Listbox.Option>
												))}
											</Listbox.Options>
										</div>
									</Listbox>

									<button
										onClick={() => setDeleteConfirm(companyName)}
										disabled={deleting}
										className={`p-1.5 rounded transition-colors ${
											isDarkMode
												? 'text-red-400 hover:bg-red-500/20'
												: 'text-red-500 hover:bg-red-50'
										} ${deleting ? 'opacity-50 cursor-not-allowed' : ''}`}
									>
										<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
											<path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
										</svg>
									</button>
								</div>
							</div>

							{isExpanded && (
								<div className={`px-4 pb-4 pt-3 border-t ${borderColor}`}>
									<div className="grid grid-cols-2 gap-3">
										{ALERT_TYPES.map(alert => {
											const isAlertUpdating = updating[`${companyName}-${alert.key}`]
											const isDisabled = isAlertUpdating
											const isOn = settings.isNotificationsOn && settings[alert.key]
											return (
												<div
													key={alert.key}
													className={`flex items-center justify-between border ${borderColor} rounded-md px-3 py-2`}
												>
													<span className={`text-xs ${textColor}`}>
														{alert.label}
													</span>
													<div
														className={`relative inline-block w-8 h-4 rounded-full transition ${
															isOn
																? 'bg-blue-500'
																: isDarkMode
																? 'bg-gray-600'
																: 'bg-gray-300'
														} ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
														onClick={() => !isDisabled && handleAlertToggle(companyName, alert.key, settings)}
													>
														<div
															className={`absolute left-0.5 top-0.5 w-3 h-3 bg-white rounded-full transition-transform ${
																isOn ? 'translate-x-4' : 'translate-x-0'
															}`}
														/>
													</div>
												</div>
											)
										})}
									</div>

									<div className="flex justify-end mt-3">
										<button
											onClick={() => handleTest(companyName, settings.selectedTelegramGroup)}
											disabled={testing[companyName] || !settings.selectedTelegramGroup || !settings.isNotificationsOn}
											className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded transition-colors ${
												isDarkMode
													? 'bg-blue-600 hover:bg-blue-700 text-white'
													: 'bg-blue-500 hover:bg-blue-600 text-white'
											} ${(!settings.selectedTelegramGroup || !settings.isNotificationsOn) ? 'opacity-50 cursor-not-allowed' : ''}`}
										>
											Send test notifications
											{testing[companyName] ? (
												<svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
													<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
													<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
												</svg>
											) : (
												<svg className="w-3.5 h-3.5 rotate-90" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
													<path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
												</svg>
											)}
										</button>
									</div>
								</div>
							)}
						</div>
					)
				})}
			</div>

			{deleteConfirm && (
				<div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 pt-20 p-4">
					<div className={`${cardBg} rounded-lg w-full max-w-md overflow-hidden`}>
						<div className="p-6">
							<div className="flex items-start gap-3 mb-4">
								<div className={`w-10 h-10 ${isDarkMode ? 'bg-red-500/10' : 'bg-red-50'} rounded-full flex items-center justify-center shrink-0`}>
									<svg className={`w-5 h-5 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
										<path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
									</svg>
								</div>
								<div>
									<h3 className={`text-base font-semibold ${textColor}`}>
										Delete company notifications
									</h3>
									<p className={`text-sm ${subTextColor} mt-1`}>
										Are you sure you want to delete notifications for <span className="font-medium">{formatCompanyName(deleteConfirm)}</span>? This action cannot be undone.
									</p>
								</div>
							</div>
						</div>

						<div className={`px-6 py-4 flex justify-end gap-3 ${isDarkMode ? 'border-t border-gray-700' : 'bg-gray-50 border-t border-gray-100'}`}>
							<button
								onClick={() => setDeleteConfirm(null)}
								disabled={deleting}
								className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
									isDarkMode
										? 'text-gray-300 hover:bg-gray-700'
										: 'text-gray-700 hover:bg-gray-100'
								}`}
							>
								Cancel
							</button>
							<button
								onClick={handleDeleteConfirm}
								disabled={deleting}
								className="px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 transition-colors"
							>
								{deleting ? 'Deleting...' : 'Delete'}
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	)
}

export default CompanyNotificationsCard
